import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import SparaButton from '../SparaButton/SparaButton';
import styles from './ConfirmDialog.module.scss';

const WarningIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M12 2L2 20h20L12 2z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <line
            x1="12"
            y1="9"
            x2="12"
            y2="13"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
        <circle cx="12" cy="17" r="1" fill="currentColor" />
    </svg>
);

const DangerIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <line
            x1="12"
            y1="7"
            x2="12"
            y2="13"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
        <circle cx="12" cy="17" r="1" fill="currentColor" />
    </svg>
);

const SuccessIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <polyline
            points="7,12 10,15 17,9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const InfoIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <line x1="12" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="12" y1="12" x2="12" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const icons = {
    warning: WarningIcon,
    danger: DangerIcon,
    success: SuccessIcon,
    info: InfoIcon,
};

const ConfirmDialog = ({
    open,
    title,
    message,
    onConfirm,
    onCancel,
    confirmText = 'Já',
    cancelText = 'Hætta við',
    type = 'warning',
    testId,
    children,
}) => {
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    if (!open) return null;

    const Icon = icons[type] || icons.warning;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && onCancel) {
            onCancel();
        }
    };

    return ReactDOM.createPortal(
        <div
            className={styles.backdrop}
            onClick={handleBackdropClick}
            data-testid={testId || undefined}
        >
            <div className={`${styles.dialog} ${styles[type]}`} role="dialog" aria-modal="true">
                <div className={`${styles.iconWrap} ${styles[`icon_${type}`]}`}>
                    <Icon />
                </div>
                <h2 className={styles.title} data-testid={testId ? `${testId}-title` : undefined}>
                    {title}
                </h2>
                {message && (
                    <p
                        className={styles.message}
                        data-testid={testId ? `${testId}-message` : undefined}
                    >
                        {message}
                    </p>
                )}
                {children ? <div className={styles.extra}>{children}</div> : null}
                <div className={styles.actions}>
                    {onCancel && (
                        <SparaButton
                            variant="outlined"
                            color="secondary"
                            size="medium"
                            style={{ borderColor: '#16b4f3', color: '#16b4f3' }}
                            onClick={onCancel}
                            data-testid={testId ? `${testId}-cancel` : undefined}
                        >
                            {cancelText}
                        </SparaButton>
                    )}
                    <SparaButton
                        variant="filled"
                        color={
                            type === 'danger'
                                ? 'danger'
                                : type === 'warning'
                                ? 'primary'
                                : 'secondary'
                        }
                        size="medium"
                        onClick={onConfirm}
                        data-testid={testId ? `${testId}-confirm` : undefined}
                    >
                        {confirmText}
                    </SparaButton>
                </div>
            </div>
        </div>,
        document.body
    );
};

ConfirmDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    message: PropTypes.node,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    type: PropTypes.oneOf(['warning', 'danger', 'success', 'info']),
    testId: PropTypes.string,
    children: PropTypes.node,
};

export default ConfirmDialog;
